'use client';

/**
 * InlineStatus Component
 *
 * Stripe-inspired inline status indicator for operations.
 * Shows small, contextual feedback for completed/failed operations.
 *
 * Usage:
 * ```tsx
 * // Simple usage
 * <InlineStatus status="success" message="Saved!" />
 *
 * // With auto-hide
 * <InlineStatus status="success" message="Copied!" autoHide autoHideDelay={2000} />
 *
 * // Loading state
 * <InlineStatus status="loading" message="Saving..." />
 * ```
 */

import SuccessIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import { useEffect, useState } from 'react';
import { baseColors } from '../../theme/palette';

// ============================================================================
// Types
// ============================================================================

export type InlineStatusType = 'success' | 'error' | 'warning' | 'info' | 'loading' | 'idle';

export interface InlineStatusProps {
  /** Status type */
  status: InlineStatusType;

  /** Message to display */
  message?: string;

  /** Auto-hide after displaying */
  autoHide?: boolean;

  /** Auto-hide delay in ms */
  autoHideDelay?: number;

  /** Callback when auto-hide triggers */
  onHide?: () => void;

  /** Show icon */
  showIcon?: boolean;

  /** Size variant */
  size?: 'small' | 'medium';

  /** Additional className */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StatusContainerProps {
  $status: InlineStatusType;
  $isDark: boolean;
  $size: 'small' | 'medium';
}

const statusColors: Record<InlineStatusType, { color: string; bg: string }> = {
  success: {
    color: baseColors.success[500],
    bg: baseColors.success[50],
  },
  error: {
    color: baseColors.error[500],
    bg: baseColors.error[50],
  },
  warning: {
    color: baseColors.warning[600],
    bg: baseColors.warning[50],
  },
  info: {
    color: baseColors.primary[500],
    bg: baseColors.primary[50],
  },
  loading: {
    color: baseColors.neutral[500],
    bg: baseColors.neutral[50],
  },
  idle: {
    color: baseColors.neutral[400],
    bg: 'transparent',
  },
};

const StatusContainer = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StatusContainerProps>(({ theme, $status, $isDark, $size }) => {
  const colors = statusColors[$status];
  const isMaterial = theme.designGuideline === 'material';

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: $size === 'small' ? theme.spacing(0.5) : theme.spacing(1),
    padding: $size === 'small' ? theme.spacing(0.25, 0.75) : theme.spacing(0.5, 1),
    borderRadius: isMaterial ? 4 : 6, // MD3 uses smaller radius for compact components
    backgroundColor: $isDark ? alpha(colors.color, 0.1) : alpha(colors.bg, 0.8),
    color: colors.color,
  };
});

// ============================================================================
// Component
// ============================================================================

const statusIcons: Record<InlineStatusType, React.ReactNode> = {
  success: <SuccessIcon fontSize="inherit" />,
  error: <ErrorIcon fontSize="inherit" />,
  warning: <WarningIcon fontSize="inherit" />,
  info: <InfoIcon fontSize="inherit" />,
  loading: <CircularProgress size={12} color="inherit" />,
  idle: null,
};

export const InlineStatus: React.FC<InlineStatusProps> = ({
  status,
  message,
  autoHide = false,
  autoHideDelay = 3000,
  onHide,
  showIcon = true,
  size = 'small',
  className,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (autoHide && status !== 'idle' && status !== 'loading') {
      const timer = setTimeout(() => {
        setVisible(false);
        onHide?.();
      }, autoHideDelay);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [autoHide, autoHideDelay, onHide, status]);

  // Reset visibility when status changes
  useEffect(() => {
    setVisible(true);
  }, [status]);

  if (status === 'idle' || !visible) {
    return null;
  }

  const icon = statusIcons[status];

  return (
    <AnimatePresence>
      {visible && (
        <StatusContainer
          $status={status}
          $isDark={isDark}
          $size={size}
          className={className}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.15 }}
        >
          {showIcon && icon && (
            <span style={{ display: 'flex', alignItems: 'center', fontSize: size === 'small' ? 14 : 18 }}>{icon}</span>
          )}
          {message && (
            <Typography
              variant={size === 'small' ? 'caption' : 'body2'}
              sx={{ fontWeight: 500, lineHeight: 1, color: 'inherit' }}
            >
              {message}
            </Typography>
          )}
        </StatusContainer>
      )}
    </AnimatePresence>
  );
};

export default InlineStatus;
